import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { hsnApiService, HsnCodeCreateRequest } from '../services/hsnApi';

interface CreateHsnDialogProps {
  onHsnCreated: () => void;
}

export function CreateHsnDialog({ onHsnCreated }: CreateHsnDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<HsnCodeCreateRequest>({
    hsnCode: '',
    description: '',
    chapter: '',
    heading: '',
    subHeading: '',
    currentIgstRate: 0,
    currentCgstRate: 0,
    currentSgstRate: 0,
    currentCessRate: 0,
    isActive: true,
    branchId: 1, // Default branch ID
  });

  const handleInputChange = (field: keyof HsnCodeCreateRequest, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.hsnCode.trim()) {
      toast.error('HSN code is required');
      return;
    }

    if (!formData.chapter.trim()) {
      toast.error('Chapter is required');
      return;
    }

    if (!formData.heading.trim()) {
      toast.error('Heading is required');
      return;
    }

    if (formData.currentIgstRate < 0 || formData.currentIgstRate > 100) {
      toast.error('IGST rate must be between 0 and 100');
      return;
    }

    if (formData.currentCgstRate < 0 || formData.currentCgstRate > 100) {
      toast.error('CGST rate must be between 0 and 100');
      return;
    }

    if (formData.currentSgstRate < 0 || formData.currentSgstRate > 100) {
      toast.error('SGST rate must be between 0 and 100');
      return;
    }

    setLoading(true);
    try {
      await hsnApiService.createHsnCode(formData);
      setFormData({
        hsnCode: '',
        description: '',
        chapter: '',
        heading: '',
        subHeading: '',
        currentIgstRate: 0,
        currentCgstRate: 0,
        currentSgstRate: 0,
        currentCessRate: 0,
        isActive: true,
        branchId: 1,
      });
      setOpen(false);
      onHsnCreated();
    } catch (error) {
      // console.error('Error creating HSN code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      hsnCode: '',
      description: '',
      chapter: '',
      heading: '',
      subHeading: '',
      currentIgstRate: 0,
      currentCgstRate: 0,
      currentSgstRate: 0,
      currentCessRate: 0,
      isActive: true,
      branchId: 1,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2 border-primary-200 text-primary-600 hover:bg-primary-50"
        >
          <Plus className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New HSN Code</DialogTitle>
          <DialogDescription>
            Add a new HSN code for raw items. Fill in the details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hsnCode">HSN Code *</Label>
              <Input
                id="hsnCode"
                value={formData.hsnCode}
                onChange={(e) => handleInputChange('hsnCode', e.target.value)}
                placeholder="Enter HSN code"
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="chapter">Chapter *</Label>
              <Input
                id="chapter"
                value={formData.chapter}
                onChange={(e) => handleInputChange('chapter', e.target.value)}
                placeholder="Enter chapter"
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="heading">Heading *</Label>
              <Input
                id="heading"
                value={formData.heading}
                onChange={(e) => handleInputChange('heading', e.target.value)}
                placeholder="Enter heading"
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subHeading">Sub Heading</Label>
              <Input
                id="subHeading"
                value={formData.subHeading}
                onChange={(e) => handleInputChange('subHeading', e.target.value)}
                placeholder="Enter sub heading"
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter HSN code description"
              className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              disabled={loading}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currentIgstRate">IGST Rate (%) *</Label>
              <Input
                id="currentIgstRate"
                value={formData.currentIgstRate}
                onChange={(e) => handleInputChange('currentIgstRate', parseFloat(e.target.value) || 0)}
                placeholder="Enter IGST rate"
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="currentCgstRate">CGST Rate (%) *</Label>
              <Input
                id="currentCgstRate"
                value={formData.currentCgstRate}
                onChange={(e) => handleInputChange('currentCgstRate', parseFloat(e.target.value) || 0)}
                placeholder="Enter CGST rate"
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currentSgstRate">SGST Rate (%) *</Label>
              <Input
                id="currentSgstRate"
                value={formData.currentSgstRate}
                onChange={(e) => handleInputChange('currentSgstRate', parseFloat(e.target.value) || 0)}
                placeholder="Enter SGST rate"
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="currentCessRate">CESS Rate (%)</Label>
              <Input
                id="currentCessRate"
                value={formData.currentCessRate}
                onChange={(e) => handleInputChange('currentCessRate', parseFloat(e.target.value) || 0)}
                placeholder="Enter CESS rate"
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                disabled={loading}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => handleInputChange('isActive', checked)}
              disabled={loading}
            />
            <Label htmlFor="isActive" className="text-sm">
              Active
            </Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="border-primary-200 text-primary-600 hover:bg-primary-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
            >
              {loading ? 'Creating...' : 'Create HSN Code'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
